import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowDown,
    faArrowUp,
    faChevronDown,
    faChevronRight,
    faEyeSlash,
    faExchangeAlt,
} from '@fortawesome/free-solid-svg-icons';

import { stockService } from '../services/stockService';
import RecordLoadProblem, { describeLoadFailure } from './ui/RecordLoadProblem';
import './StockVariantMovementPanel.css';

/**
 * "Where has this product been traded?" -- every deal one product variant was
 * bought and sold in, on the product card itself.
 *
 * Two rules this panel exists to keep:
 *
 *  1. It asks the server ONLY when someone opens it. The Product Management
 *     screen lists many variants at once; fetching movement for all of them up
 *     front would be hundreds of requests for panels nobody looked at.
 *  2. A load that FAILED never renders as "this product has not been traded
 *     yet". That is row 125's standing bug class -- a refused or crashed load
 *     drawn as an empty record -- so the failure goes through the shared
 *     `RecordLoadProblem` card, with its Try again button, exactly as the
 *     detail screens do.
 */

/**
 * Which dates a desk actually records.
 *
 * Bunkers, lubes and cargo fuels deliver against a Bunker Delivery Note; coal
 * delivers against a Bill of Lading and never has a BDN. Driving the date
 * chips off this map is why a coal row does not carry an empty "BDN --", which
 * would read as "the BDN is missing" rather than "coal has no BDN".
 *
 * `dates.delivery` is deliberately NOT here. The server sends it and it is
 * permanently null -- nothing in HMS records when a delivery actually
 * completed -- so a "Delivered" chip was a dash on every row of every product,
 * forever. A dash reads as "somebody should go and type this in", which is a
 * different sentence from "the system never captures this", and it is the
 * wrong one. The panel says the true sentence once, in DATE_FOOTNOTE below,
 * instead of printing an empty field on every row.
 */
const DESK_DATE_FIELDS = {
    bunkers: [['eta', 'ETA'], ['bdn', 'BDN']],
    lubes: [['eta', 'ETA'], ['bdn', 'BDN']],
    cargo_fuels: [['eta', 'ETA'], ['bdn', 'BDN']],
    cargo_coal: [['bln', 'BLN']],
};

const ALL_DATE_FIELDS = [
    ['eta', 'ETA'], ['bdn', 'BDN'], ['bln', 'BLN'],
];

const DESK_LABELS = {
    bunkers: 'Bunkers',
    lubes: 'Lubes',
    cargo_coal: 'Coal',
    cargo_fuels: 'Cargo fuels',
};

/**
 * The date chips for one row.
 *
 * A desk we have never heard of must not silently take the bunkers path, so it
 * falls back to showing whichever dates the row actually carries -- never an
 * invented blank.
 */
const dateFieldsForRow = (row) => {
    const known = DESK_DATE_FIELDS[row.desk];
    if (known) return known;
    return ALL_DATE_FIELDS.filter(([key]) => row.dates && row.dates[key]);
};

const isBlank = (value) => value === null || value === undefined || value === '';

/**
 * Show the server's number, not our own rounding of it.
 *
 * Quantities and prices arrive as decimal STRINGS at the precision the deal
 * screens show (a bunker price is quoted to 4 places, a cargo quantity to 3).
 * Running them through Number() and back would silently disagree with the deal
 * the row points at, so only the thousands separators are added and the digits
 * are left exactly as they came.
 */
const formatDecimalString = (value) => {
    if (isBlank(value)) return null;
    const text = String(value).trim();
    const match = text.match(/^(-?)(\d+)(\.\d+)?$/);
    if (!match) return text;
    const [, sign, whole, fraction] = match;
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${sign}${grouped}${fraction || ''}`;
};

const DASH = '—';

/**
 * One quiet line under the whole list, never per row.
 *
 * It has to be true on all four desks at once: bunkers, lubes and cargo fuels
 * carry a BDN, coal carries a BLN, so the sentence names both rather than
 * writing bunkers-only wording onto a coal row.
 */
const DATE_FOOTNOTE = 'IMT records no separate \u201cdelivery completed\u201d date. '
    + `The signed note date on each row ${DASH} BDN, or BLN on coal ${DASH} is `
    + 'the closest record of when the deal actually happened.';

/**
 * A bunkers trader types a bare number: a live enquiry genuinely records no
 * currency until it is recapped, and the rule deliberately refuses to guess
 * USD. So a price with no currency is shown WITH that fact spelled out --
 * "61.5000/MT" and, under it, "currency not recorded" -- rather than as a bare
 * number that reads like a field we dropped on the way to the screen.
 *
 * The note is returned SEPARATELY from the figure because the two need
 * different typesetting. Figures are numbers and stay on one line; the note is
 * a sentence, and when it shared that one-line rule the narrow figure column
 * clipped it to "(curre..." -- which tells the reader nothing at all.
 */
const formatPrice = (row) => {
    const amount = formatDecimalString(row.price);
    if (!amount) return { text: DASH, note: null };
    const per = row.price_unit ? `/${row.price_unit}` : '';
    if (!row.currency) return { text: `${amount}${per}`, note: 'currency not recorded' };
    return { text: `${amount} ${row.currency}${per}`, note: null };
};

const formatQuantity = (row) => {
    const amount = formatDecimalString(row.quantity);
    if (!amount) return DASH;
    return row.unit ? `${amount} ${row.unit}` : amount;
};

const formatTotal = (value, unit) => {
    const amount = formatDecimalString(value);
    if (!amount) return DASH;
    return unit ? `${amount} ${unit}` : amount;
};

const formatDate = (value) => {
    if (isBlank(value)) return DASH;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
};

const MovementRow = ({ row }) => {
    const isPurchase = row.direction === 'purchase';
    // Told apart by a word and an arrow as well as by colour -- colour alone is
    // no answer for anyone who cannot see the difference.
    const directionLabel = isPurchase ? 'Purchase' : 'Sale';
    const price = formatPrice(row);

    const body = (
        <>
            <div className="variant-movement__row-main">
                <span
                    className={`variant-movement__direction variant-movement__direction--${isPurchase ? 'purchase' : 'sale'}`}
                >
                    <FontAwesomeIcon icon={isPurchase ? faArrowDown : faArrowUp} />
                    {' '}{directionLabel}
                </span>
                <span className="variant-movement__desk">
                    {DESK_LABELS[row.desk] || row.desk}
                </span>
                <span className="variant-movement__reference">
                    {row.reference || DASH}
                </span>
            </div>

            <div className="variant-movement__row-figures">
                <span className="variant-movement__figure">
                    <span className="variant-movement__figure-label">Price</span>
                    <span className="variant-movement__figure-value">{price.text}</span>
                    {price.note && (
                        <span
                            className="variant-movement__figure-note"
                            data-testid="movement-price-note"
                        >
                            {price.note}
                        </span>
                    )}
                </span>
                <span className="variant-movement__figure">
                    <span className="variant-movement__figure-label">Quantity</span>
                    <span className="variant-movement__figure-value">{formatQuantity(row)}</span>
                </span>
                <span className="variant-movement__figure">
                    <span className="variant-movement__figure-label">Port</span>
                    <span className="variant-movement__figure-value">{row.port || DASH}</span>
                </span>
                <span className="variant-movement__figure">
                    <span className="variant-movement__figure-label">Counterparty</span>
                    <span className="variant-movement__figure-value">{row.counterparty || DASH}</span>
                </span>
            </div>

            <div className="variant-movement__row-dates">
                {dateFieldsForRow(row).map(([key, label]) => (
                    <span className="variant-movement__date" key={key}>
                        <span className="variant-movement__date-label">{label}</span>
                        <span className="variant-movement__date-value">
                            {formatDate(row.dates ? row.dates[key] : null)}
                        </span>
                    </span>
                ))}
                {dateFieldsForRow(row).length === 0 && (
                    <span className="variant-movement__date variant-movement__muted">
                        No dates recorded yet
                    </span>
                )}
            </div>
        </>
    );

    if (row.delivery_url) {
        return (
            <Link
                to={row.delivery_url}
                className="variant-movement__row variant-movement__row--linked"
                data-testid="movement-row"
            >
                {body}
            </Link>
        );
    }

    // No delivery exists yet, so there is nothing to open. Saying so beats a
    // row that looks clickable and does nothing when it is clicked.
    return (
        <div className="variant-movement__row" data-testid="movement-row">
            {body}
            <span className="variant-movement__no-delivery">No delivery yet</span>
        </div>
    );
};

const StockVariantMovementPanel = ({ variantId }) => {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    // The REASON the load failed, kept apart from the rows. Catching an error
    // and leaving `data` as an empty list is precisely the bug this panel is
    // not allowed to have.
    const [problem, setProblem] = useState(null);

    const load = useCallback(async () => {
        if (!variantId) return;
        setLoading(true);
        setProblem(null);
        try {
            const answer = await stockService.getVariantMovement(variantId);
            setData(answer);
        } catch (err) {
            setData(null);
            setProblem(describeLoadFailure(err));
        } finally {
            setLoading(false);
        }
    }, [variantId]);

    const toggle = useCallback(() => {
        const opening = !open;
        setOpen(opening);
        // Collapsed by default; the first open is what fetches. Re-opening a
        // panel that already has its rows does not ask again.
        if (opening && !data && !loading) load();
    }, [open, data, loading, load]);

    const rows = data?.rows || [];
    const totals = data?.totals || {};
    const hiddenCount = data?.hidden_row_count || 0;

    return (
        <div className="variant-movement">
            <button
                type="button"
                className="variant-movement__toggle"
                onClick={toggle}
                aria-expanded={open}
            >
                <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
                <FontAwesomeIcon icon={faExchangeAlt} className="variant-movement__toggle-icon" />
                Movement
            </button>

            {open && (
                <div className="variant-movement__body">
                    {loading && (
                        <p className="variant-movement__state">Loading movement…</p>
                    )}

                    {/* Row 125's shared card: a refused or broken load is shown
                        INSTEAD of the list, never alongside an empty one. */}
                    {!loading && problem && (
                        <div className="variant-movement__problem">
                            <RecordLoadProblem
                                problem={problem}
                                recordName="product's movement"
                                areaName="this product's deals"
                                onRetry={load}
                                failedText="Something went wrong while opening it. Try again."
                            />
                        </div>
                    )}

                    {/* Three different answers, three different sentences. An
                        empty list means "never traded" ONLY when nothing was
                        withheld; when everything was withheld it is the
                        opposite fact, and printing the untraded sentence above
                        the withheld count told a trader the product had never
                        been bought or sold when it had. */}
                    {!loading && !problem && data && rows.length === 0 && hiddenCount === 0 && (
                        <p className="variant-movement__state variant-movement__empty">
                            This product has not been traded yet.
                        </p>
                    )}

                    {!loading && !problem && data && rows.length === 0 && hiddenCount > 0 && (
                        <p
                            className="variant-movement__state variant-movement__all-hidden"
                            role="status"
                            data-testid="movement-all-hidden"
                        >
                            <FontAwesomeIcon icon={faEyeSlash} />{' '}
                            This product HAS been traded.{' '}
                            {hiddenCount === 1
                                ? 'Its one deal is on a desk you do not have access to, so it is not shown here.'
                                : `All ${hiddenCount} of its deals are on desks you do not have access to, so none of them are shown here.`}
                        </p>
                    )}

                    {!loading && !problem && rows.length > 0 && (
                        <>
                            <div className="variant-movement__rows">
                                {rows.map((row) => (
                                    <MovementRow key={row.id} row={row} />
                                ))}
                            </div>

                            <div className="variant-movement__totals">
                                <span>
                                    Bought:{' '}
                                    <strong>
                                        {formatTotal(totals.purchased_quantity, totals.unit)}
                                    </strong>
                                </span>
                                <span>
                                    Sold:{' '}
                                    <strong>
                                        {formatTotal(totals.sold_quantity, totals.unit)}
                                    </strong>
                                </span>
                            </div>

                            {/* A total that silently adds barrels to tonnes is a
                                wrong number wearing a clean number's clothes. */}
                            {totals.mixed_units && (
                                <p className="variant-movement__caveat" role="status">
                                    This product has been traded in more than one unit.
                                    These totals cover {totals.unit || 'the main unit'} only.
                                </p>
                            )}

                            {/* Said once, at the foot of the list -- not as an
                                always-empty field repeated on every row. */}
                            <p
                                className="variant-movement__date-note"
                                data-testid="movement-date-note"
                            >
                                {DATE_FOOTNOTE}
                            </p>
                        </>
                    )}

                    {/* Never round the history down to what this person may see:
                        say plainly that there is more of it. "Further" only
                        makes sense next to rows that ARE shown -- with none
                        shown, the all-hidden sentence above says it instead. */}
                    {!loading && !problem && data && rows.length > 0 && hiddenCount > 0 && (
                        <p className="variant-movement__hidden" role="status">
                            <FontAwesomeIcon icon={faEyeSlash} />{' '}
                            {hiddenCount} further {hiddenCount === 1 ? 'deal' : 'deals'} you
                            {' '}do not have access to {hiddenCount === 1 ? 'is' : 'are'} not shown.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default StockVariantMovementPanel;
